/**
 * Pré-visualização completa do laudo + edição conversacional (E23.F8 — ADR-0025). // ux-neutrality:allow doc-comment
 *
 * Busca o documento montado (todas as contestadas) e a trilha de alterações; // ux-neutrality:allow doc-comment
 * delega cada bloco ao `LaudoPreviewContestada`. Re-busca quando uma alteração // ux-neutrality:allow doc-comment
 * é confirmada ou quando o pai sinaliza mudança (`reloadToken`). // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import { LaudoError } from '@/lib/laudo';
import {
  getLaudoPreview,
  listLaudoEdits,
  type LaudoEditRecordData,
  type LaudoPreviewData,
} from '@/lib/laudo_edits';
import { LaudoPreviewContestada } from '@/components/LaudoPreviewContestada';
import { LaudoPdfPreview } from '@/components/LaudoPdfPreview';
import { LaudoEditHistory } from '@/components/LaudoEditHistory';

interface LaudoPreviewProps {
  caseId: string;
  /** Incrementado pelo pai quando uma narrativa muda fora do preview (editor legado). */
  reloadToken?: number;
}

export function LaudoPreview({ caseId, reloadToken = 0 }: LaudoPreviewProps): JSX.Element {
  const t = ptBR.laudoCaso.preview;
  const client = useApiClient();
  const { role } = useCurrentRole();
  const isOwner = role === 'owner';

  const [preview, setPreview] = useState<LaudoPreviewData | null>(null);
  const [edits, setEdits] = useState<LaudoEditRecordData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emptyDraft, setEmptyDraft] = useState(false);
  const [internalToken, setInternalToken] = useState(0);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setEmptyDraft(false);
      try {
        const [p, e] = await Promise.all([
          getLaudoPreview(client, caseId),
          listLaudoEdits(client, caseId),
        ]);
        if (!cancelled) {
          setPreview(p);
          setEdits(e);
        }
      } catch (err) {
        if (cancelled) return;
        // E28.F1.T03: rascunho sem narrativa (422) é estado normal do fluxo,
        // não falha — mesma distinção que o LaudoPdfPreview já faz.
        const reason = err instanceof LaudoError ? err.reason : 'unknown';
        if (reason === 'narrative_not_ready' || reason === 'not_ready') {
          setEmptyDraft(true);
        } else {
          setError(t.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, client, reloadToken, internalToken, t.error]);

  const handleEdited = useCallback(() => setInternalToken((n) => n + 1), []);

  return (
    <section
      data-laudo-section="preview"
      data-testid="laudo-preview"
      className="flex flex-col gap-sc-4"
    >
      {/* E23.F23: sem título "Pré-visualização do laudo" (redundante — o PDF
          abaixo já é o documento). A guidance de edição vive nos próprios
          controles ("Pedir alteração", "Editar trecho"). */}
      {/* E23.F23 (revisão owner): coluna única — o PDF PAGINADO (1 página por vez)
          grande no topo em largura de leitura confortável, e as ferramentas de
          edição em largura cheia abaixo. Side-by-side deixava o PDF minúsculo.
          Re-renderiza a cada alteração confirmada (reloadToken + internalToken). */}
      <LaudoPdfPreview caseId={caseId} reloadToken={reloadToken + internalToken} />

      <div
        className="flex flex-col gap-sc-4"
        data-testid="laudo-preview-edit-column"
        aria-label={t.editColumnLabel}
      >
        {loading && <p className="text-body-sm text-fg-muted">{t.loading}</p>}
        {error && (
          <p role="alert" className="text-body-sm text-danger-700">
            {error}
          </p>
        )}
        {!loading && emptyDraft && (
          <p className="text-body-sm text-fg-muted" data-testid="laudo-preview-empty">
            {t.empty}
          </p>
        )}
        {!loading && !error && preview && (
          <>
            {preview.contestadas.map((contestada) => (
              <LaudoPreviewContestada
                key={contestada.case_analysis_id}
                caseId={caseId}
                contestada={contestada}
                isOwner={isOwner}
                onEdited={handleEdited}
              />
            ))}
            <LaudoEditHistory edits={edits} />
          </>
        )}
      </div>
    </section>
  );
}
